import { supabase } from '@/integrations/supabase/client';

// Development-only logging utility
const DEV_LOGS_ENABLED = import.meta.env.DEV || localStorage.getItem('enable_realtime_logs') === 'true';
const realtimeLog = (...args: any[]) => {
  if (DEV_LOGS_ENABLED) console.log(...args);
};

/**
 * RealtimeConnectionManager
 * - Strict singleton managing a single shared Supabase Realtime channel
 * - Centralizes subscribe/unsubscribe, retry with exponential backoff, and cleanup
 * - Prevents duplicate channels and duplicate retry scheduling across the app
 */
export class RealtimeConnectionManager {
  private static instance: RealtimeConnectionManager | null = null;

  static getInstance(): RealtimeConnectionManager {
    if (RealtimeConnectionManager.instance) return RealtimeConnectionManager.instance;
    RealtimeConnectionManager.instance = new RealtimeConnectionManager();
    return RealtimeConnectionManager.instance;
  }

  private constructor() {}

  private topic = 'global-notifications';
  private channel: any = null;

  // Subscriptions registry to rebind on reconnect
  private subs = new Map<string, {
    filter: { event: '*' | 'INSERT' | 'UPDATE' | 'DELETE'; schema: string; table: string; filter?: string };
    callback: (payload?: any) => void;
    active: boolean;
  }>();
  private idCounter = 0;

  // Ref counting across components/pages
  private refCount = 0;

  // Connection state
  private isConnecting = false;
  private isConnected = false;
  private isShuttingDown = false;
  private suppressFailureHandling = false;

  // Retry management (single global backoff)
  private retryIndex = 0; // 0..2 => 10s, 20s, 40s
  private retryTimerId: number | null = null;
  private readonly backoffMs = [10_000, 20_000, 40_000];

  // Polling fallback when Realtime fails
  private isPolling = false;
  private pollingTimerId: number | null = null;
  private readonly pollingIntervalMs = 30_000; // 30 seconds (reduced from 15s)
  private lastDataSnapshots = new Map<string, string>(); // table -> JSON hash of last data

  // Optional status listeners
  private statusListeners = new Set<(status: 'SUBSCRIBED' | 'CLOSED' | 'CHANNEL_ERROR' | 'TIMED_OUT') => void>();

  onStatusChange(cb: (status: 'SUBSCRIBED' | 'CLOSED' | 'CHANNEL_ERROR' | 'TIMED_OUT') => void) {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  getChannelsCount(): number {
    const chans = (supabase as any).getChannels ? (supabase as any).getChannels() : [];
    return Array.isArray(chans) ? chans.length : 0;
  }

  addPostgresSubscription(
    filter: { event: '*' | 'INSERT' | 'UPDATE' | 'DELETE'; schema: string; table: string; filter?: string },
    callback: (payload?: any) => void,
  ): () => void {
    const id = `sub_${++this.idCounter}`;
    console.log(`➕ RealtimeConnectionManager: Adding subscription ${id} for table ${filter.table} (refCount: ${this.refCount} → ${this.refCount + 1})`);
    this.subs.set(id, { filter, callback, active: true });
    this.refCount++;
    // Ensure the channel is up and bound
    this.ensureConnected();
    this.bindOne(id);
    return () => {
      const record = this.subs.get(id);
      if (record && record.active) {
        console.log(`➖ RealtimeConnectionManager: Removing subscription ${id} for table ${filter.table} (refCount: ${this.refCount} → ${this.refCount - 1})`);
        record.active = false;
        this.subs.delete(id);
        this.refCount = Math.max(0, this.refCount - 1);
        // Close channel if nobody is listening anymore
        this.releaseIfNoSubscribers();
      }
    };
  }

  releaseIfNoSubscribers() {
    if (this.refCount === 0) {
      console.log('🔌 RealtimeConnectionManager: No more subscribers, closing channel');
      this.closeChannel();
    } else {
      console.log(`🔄 RealtimeConnectionManager: Still ${this.refCount} active subscription(s), keeping channel open`);
    }
  }

  forceCleanup() {
    // Forcefully remove all and close
    this.subs.clear();
    this.refCount = 0;
    this.closeChannel();
  }

  private bindOne(id: string) {
    if (!this.channel) return;
    const entry = this.subs.get(id);
    if (!entry || !entry.active) return;
    const { filter, callback } = entry;
    try {
      this.channel = this.channel.on('postgres_changes', filter as any, callback as any);
    } catch (e) {
      console.warn('Failed to bind subscription', id, e);
    }
  }

  private bindAll() {
    if (!this.channel) return;
    this.subs.forEach((_v, id) => this.bindOne(id));
  }

  ensureConnected(topic: string = this.topic) {
    // Reuse existing active channel
    if (this.channel && this.isConnected) {
      console.log('🔁 RealtimeConnectionManager: Reusing existing connected channel');
      return;
    }
    if (this.isConnecting) {
      console.log('⏳ RealtimeConnectionManager: Connection already in progress');
      return;
    }

    // Detect iOS/mobile environment
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isCapacitor = !!(window as any).Capacitor;
    
    if (isIOS || isCapacitor) {
      console.log('📱 RealtimeConnectionManager: iOS/Capacitor detected - using polling mode');
      // Start polling immediately on iOS instead of attempting WebSocket
      this.startPolling();
      return;
    }

    // Reuse existing channel from client if present
    const existing = (supabase as any).getChannels ? (supabase as any).getChannels() : [];
    console.log(`🔍 RealtimeConnectionManager: Found ${existing.length} existing channels in Supabase client`);
    
    const existingChannel = Array.isArray(existing)
      ? existing.find((c: any) => typeof c.topic === 'string' && c.topic.includes(topic))
      : null;

    if (existingChannel) {
      console.log(`♻️ RealtimeConnectionManager: Reusing existing channel "${topic}"`);
      this.channel = existingChannel;
      this.isConnected = true;
      this.isConnecting = false;
      this.retryIndex = 0;
      if (this.retryTimerId) { clearTimeout(this.retryTimerId); this.retryTimerId = null; }
      return;
    }

    // Create new channel and subscribe with guarded status handler
    console.log(`🆕 RealtimeConnectionManager: Creating NEW channel "${topic}" (current total: ${existing.length})`);
    this.isConnecting = true;
    this.channel = supabase.channel(topic, {
      config: {
        broadcast: { self: false },
        presence: { key: '' },
      },
    });
    
    // Rebind all known subscriptions before subscribe
    this.bindAll();

    // Set a timeout to fall back to polling if connection takes too long
    const connectionTimeout = window.setTimeout(() => {
      if (this.isConnecting && !this.isConnected) {
        console.warn('⏱️ RealtimeConnectionManager: Connection timeout, falling back to polling');
        this.isConnecting = false;
        this.startPolling();
      }
    }, 5000); // 5 second timeout

    this.channel.subscribe((status: any, error: any) => {
      const channelCount = (supabase as any).getChannels ? (supabase as any).getChannels().length : '?';
      console.log(`📡 RealtimeConnectionManager: Status ${status} (active channels: ${channelCount})`, error ? error : '');
      this.statusListeners.forEach((cb) => cb(status));

      if (status === 'SUBSCRIBED') {
        clearTimeout(connectionTimeout);
        console.log(`✅ RealtimeConnectionManager: Successfully connected to "${topic}" (total channels: ${channelCount})`);
        this.isConnected = true;
        this.isConnecting = false;
        this.retryIndex = 0;
        if (this.retryTimerId) { clearTimeout(this.retryTimerId); this.retryTimerId = null; }
        
        // Stop polling when Realtime reconnects
        if (this.isPolling) {
          console.log('🔄 RealtimeConnectionManager: Realtime reconnected, stopping polling fallback');
          this.stopPolling();
        }
        return;
      }

      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        clearTimeout(connectionTimeout);
        
        if (this.isShuttingDown || this.suppressFailureHandling) {
          console.log('🛑 RealtimeConnectionManager: Ignoring error during shutdown/cleanup');
          return;
        }
        
        // Check for permission/RLS errors - do NOT retry these
        const errorMsg = error?.message || error?.msg || JSON.stringify(error) || '';
        const isPermissionError = 
          errorMsg.includes('permission denied') ||
          errorMsg.includes('403') ||
          errorMsg.includes('row-level security') ||
          errorMsg.includes('RLS') ||
          errorMsg.includes('policy');
        
        if (isPermissionError) {
          console.error(`🚫 RealtimeConnectionManager: Permission error detected - will NOT retry. Error:`, error);
          console.warn('⚠️ Realtime disabled due to permission issue. Check RLS policies on subscribed tables.');
          this.isConnected = false;
          this.isConnecting = false;
          // Do not schedule retry for permission errors
          return;
        }
        
        console.warn(`⚠️ RealtimeConnectionManager: Connection failed with status ${status}, falling back to polling`);
        this.isConnected = false;
        this.isConnecting = false;
        
        // Immediately start polling instead of retrying WebSocket
        this.startPolling();
      }
    });
  }

  private scheduleRetry(topic: string) {
    if (this.retryTimerId) {
      console.log('⏳ Retry already scheduled, skipping duplicate');
      return;
    }
    if (this.retryIndex >= this.backoffMs.length) {
      console.warn('⚠️ RealtimeConnectionManager: Max retries reached, starting polling fallback');
      this.startPolling();
      return;
    }
    const delay = this.backoffMs[this.retryIndex] + Math.floor(Math.random() * 1000);
    this.retryIndex++;
    this.retryTimerId = window.setTimeout(() => {
      this.retryTimerId = null;
      if (this.isConnected || this.isConnecting) return;
      // Avoid retry when tab hidden to save CPU
      if (document.hidden) {
        this.scheduleRetry(topic);
        return;
      }
      // Recreate channel fresh
      this.closeChannel();
      this.ensureConnected(topic);
    }, delay);
  }

  private closeChannel() {
    if (!this.channel) return;
    const channelCount = (supabase as any).getChannels ? (supabase as any).getChannels().length : '?';
    console.log(`🚪 RealtimeConnectionManager: Closing channel (current total: ${channelCount})`);
    try {
      this.isShuttingDown = true;
      this.suppressFailureHandling = true;
      if (typeof this.channel.unsubscribe === 'function') {
        this.channel.unsubscribe();
      } else {
        (supabase as any).removeChannel?.(this.channel);
      }
      const afterCount = (supabase as any).getChannels ? (supabase as any).getChannels().length : '?';
      console.log(`✂️ RealtimeConnectionManager: Channel closed (remaining: ${afterCount})`);
    } catch (e) {
      console.warn('⚠️ RealtimeConnectionManager: Error closing realtime channel:', e);
    } finally {
      setTimeout(() => { this.suppressFailureHandling = false; }, 0);
      this.channel = null;
      this.isConnected = false;
      this.isConnecting = false;
      if (this.retryTimerId) { clearTimeout(this.retryTimerId); this.retryTimerId = null; }
      this.stopPolling(); // Stop polling when channel closes
      this.isShuttingDown = false;
    }
  }

  /**
   * Start polling fallback when Realtime is unavailable
   */
  private startPolling() {
    if (this.isPolling) {
      console.log('📊 RealtimeConnectionManager: Polling already active');
      return;
    }
    
    console.log('🔄 RealtimeConnectionManager: Starting polling fallback (30s interval)');
    this.isPolling = true;
    
    // Clear any pending retries
    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }
    
    // Immediate first poll
    this.pollSubscribedTables();
    
    // Set up interval
    this.pollingTimerId = window.setInterval(() => {
      if (!document.hidden) {
        this.pollSubscribedTables();
      }
    }, this.pollingIntervalMs);
  }

  /**
   * Stop polling fallback
   */
  private stopPolling() {
    if (!this.isPolling) return;
    
    console.log('⏹️ RealtimeConnectionManager: Stopping polling fallback');
    this.isPolling = false;
    
    if (this.pollingTimerId) {
      clearInterval(this.pollingTimerId);
      this.pollingTimerId = null;
    }
    
    this.lastDataSnapshots.clear();
  }

  /**
   * Poll all subscribed tables for updates
   */
  private async pollSubscribedTables() {
    // Guard: Don't poll if shutting down
    if (this.isShuttingDown || !this.isPolling) {
      return;
    }
    
    realtimeLog('📊 RealtimeConnectionManager: Polling subscribed tables for updates');
    
    try {
      // Group subscriptions by table
      const tableSubscriptions = new Map<string, Array<{ id: string; callback: (payload?: any) => void }>>();
      
      this.subs.forEach((entry, id) => {
        if (!entry.active) return;
        const tableName = entry.filter.table;
        if (!tableSubscriptions.has(tableName)) {
          tableSubscriptions.set(tableName, []);
        }
        tableSubscriptions.get(tableName)!.push({ id, callback: entry.callback });
      });

      // Poll each table
      for (const [tableName, callbacks] of tableSubscriptions) {
        // Check if still polling before each table query
        if (!this.isPolling || this.isShuttingDown) {
          break;
        }
        
        try {
          const { data, error } = await supabase
            .from(tableName)
            .select('*')
            .limit(1000);

          if (error) {
            console.warn(`⚠️ RealtimeConnectionManager: Error polling table ${tableName}:`, error);
            continue;
          }

          // Create snapshot hash to detect changes
          const currentSnapshot = JSON.stringify(data);
          const lastSnapshot = this.lastDataSnapshots.get(tableName);

          // Only trigger callbacks if data changed
          if (currentSnapshot !== lastSnapshot) {
            console.log(`🔔 RealtimeConnectionManager: Detected changes in table ${tableName}, notifying ${callbacks.length} subscriber(s)`);
            this.lastDataSnapshots.set(tableName, currentSnapshot);
            
            // Trigger all callbacks for this table
            callbacks.forEach(({ callback }) => {
              try {
                callback({ 
                  eventType: 'UPDATE',
                  new: data,
                  old: null,
                  table: tableName
                });
              } catch (e) {
                console.warn('⚠️ RealtimeConnectionManager: Error in polling callback:', e);
              }
            });
          }
        } catch (e) {
          console.warn(`⚠️ RealtimeConnectionManager: Exception polling table ${tableName}:`, e);
        }
      }
    } catch (error) {
      console.error('❌ RealtimeConnectionManager: Critical error during polling:', error);
    }
  }
}

export default RealtimeConnectionManager;
